import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { useTranslation } from 'react-i18next';

const TIER_LABELS = {
  starter: 'Starter',
  growth: 'Growth',
  pro: 'Pro',
  enterprise: 'Enterprise'
};

export default function UsageMeter({ usage = 0, limit = -1, tier = 'starter', overage = 0 }) {
  const { t } = useTranslation('billing');
  const unlimited = limit === -1 || limit == null;
  const percent   = unlimited ? 0 : Math.min((usage / limit) * 100, 100);
  const color     = percent >= 100 ? theme.colors.danger
                  : percent >= 80  ? theme.colors.warning
                  : theme.colors.success;

  return (
    <Wrap>
      <Row>
        <Label>{t('conversations', { defaultValue: 'Conversas este mês' })}</Label>
        <Value style={{ color }}>
          {usage.toLocaleString('pt-BR')}
          {!unlimited && ` / ${limit.toLocaleString('pt-BR')}`}
          {unlimited && ' / ' + t('unlimited', { defaultValue: 'Ilimitado' })}
        </Value>
      </Row>

      {!unlimited && (
        <>
          <Track>
            <Fill style={{ width: `${percent}%`, background: color }} />
          </Track>
          {percent >= 100 && (
            <Alert $color={theme.colors.danger}>
              {t('limitReached', { defaultValue: '🚨 Limite atingido. Faça upgrade para continuar.' })}
            </Alert>
          )}
          {percent >= 80 && percent < 100 && (
            <Alert $color={theme.colors.warning}>
              {t('nearLimit', { percent: Math.round(percent), defaultValue: `⚠️ ${Math.round(percent)}% do limite atingido.` })}
            </Alert>
          )}
        </>
      )}

      <Meta>
        <MetaItem>{t('plan', { defaultValue: 'Plano' })}: <strong>{TIER_LABELS[tier] || tier}</strong></MetaItem>
        {overage > 0 && (
          <MetaItem style={{ color: theme.colors.danger }}>
            {t('overageLabel', { defaultValue: 'Excedente' })}: <strong>{overage.toLocaleString('pt-BR')} {t('conversations', { defaultValue: 'conversas' })}</strong>
          </MetaItem>
        )}
      </Meta>
    </Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled.span`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textSecondary};
`;

const Value = styled.span`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
`;

const Track = styled.div`
  height: 8px;
  background: ${theme.colors.gray100};
  border-radius: ${theme.radii.full};
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  border-radius: ${theme.radii.full};
  transition: width 0.4s ease;
`;

const Alert = styled.p`
  font-size: ${theme.typography.xs};
  color: ${p => p.$color};
  margin: 0;
`;

const Meta = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;
